import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, EyeOff, ShieldAlert } from "lucide-react";
import {
  Button,
  Card,
  RECORD_RAIL_WIDTH,
  ResponsiveSplit,
} from "@/design-system/components/ui";
import { ConfirmModal } from "@/components/ui/PromptModals";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { extractErrorMessage } from "@/api/client";
import { useRevealInvestorSensitive } from "@/hooks/capitalMarkets/useInvestors";
import type { InvestorMaskedDto, InvestorSensitiveDto } from "@/types/investors";
import { InvestorStatusBadge } from "../../shared/components";
import { formatDate, humanizeStatus } from "../../shared/format";

interface OverviewTabProps {
  investor: InvestorMaskedDto;
  displayName: string;
  counterpartyHref: string | null;
  counterpartyKind: "Person" | "Entity" | null;
  onOpenCertifications: () => void;
}

export function InvestorOverviewTab({
  investor,
  displayName,
  counterpartyHref,
  counterpartyKind,
  onOpenCertifications,
}: OverviewTabProps) {
  return (
    // Container-driven — Table & Tile Standard §12. This was
    // `lg:grid-cols-3` + `lg:col-span-2`, which asks the WINDOW how wide
    // this box is; the window cannot answer that once the box is a tab
    // body, a drawer or a split pane.
    <ResponsiveSplit
      gap="md"
      mainMinWidth="34rem"
      railWidth={RECORD_RAIL_WIDTH}
      railGrows={false}
      main={
        <div className="flex flex-col gap-space-4">
          <IdentityCard
            investor={investor}
            displayName={displayName}
            counterpartyHref={counterpartyHref}
            counterpartyKind={counterpartyKind}
          />
          <SensitiveFieldsCard investor={investor} />
          <StatusCard investor={investor} />
          <NotesSummaryCard investor={investor} />
      </div>
      }
      rail={
        <div className="grid gap-space-4 min-w-0">
        <aside className="flex flex-col gap-space-4">
          <Card>
            <h3 className="font-display font-bold text-h3 m-0 mb-space-3">
              Quick actions
            </h3>
            <div className="flex flex-col gap-space-2">
              <Button variant="secondary" size="sm" onClick={onOpenCertifications}>
                Mark Accreditation Verified
              </Button>
              <Button variant="secondary" size="sm" onClick={onOpenCertifications}>
                Mark KYC Verified
              </Button>
            </div>
            <p className="text-caption text-gray-mid mt-space-3 mb-0">
              Verification opens the standard certification workflow on the
              Accreditation &amp; KYC tab.
            </p>
          </Card>
        </aside>
    </div>
      }
    />
  );
}

function IdentityCard({
  investor,
  displayName,
  counterpartyHref,
  counterpartyKind,
}: {
  investor: InvestorMaskedDto;
  displayName: string;
  counterpartyHref: string | null;
  counterpartyKind: "Person" | "Entity" | null;
}) {
  return (
    <Card>
      <h3 className="font-display font-bold text-h3 m-0 mb-space-3">Identity</h3>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-space-3 m-0">
        <Field label="Name" value={displayName} />
        <Field label="Investor type" value={humanizeStatus(investor.investorType)} />
        <div className="flex flex-col">
          <dt className="text-label font-display uppercase tracking-label text-gray-mid">
            Linked record
          </dt>
          <dd className="text-small text-onyx m-0">
            {counterpartyHref ? (
              <Link
                to={counterpartyHref}
                className="text-gold-shadow hover:underline"
              >
                {counterpartyKind} detail
              </Link>
            ) : (
              <span className="text-gray-mid italic">Not linked</span>
            )}
          </dd>
        </div>
        <Field
          label="Preferred contact"
          value={humanizeStatus(investor.preferredCommunicationMethod)}
        />
      </dl>
    </Card>
  );
}

function SensitiveFieldsCard({ investor }: { investor: InvestorMaskedDto }) {
  const { hasPermission } = useAuth();
  const toast = useToast();
  const canViewSensitive = hasPermission("Investor.ViewSensitiveFields");
  const reveal = useRevealInvestorSensitive(investor.id);

  const [confirmOpen, setConfirmOpen] = useState(false);
  // Plaintext lives ONLY in this local component state — never localStorage,
  // never the React Query cache, never the URL. Cleared on Hide + on unmount.
  const [revealed, setRevealed] = useState<InvestorSensitiveDto | null>(null);

  useEffect(() => {
    return () => {
      setRevealed(null);
      reveal.reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirmReveal = () => {
    setConfirmOpen(false);
    reveal
      .mutateAsync()
      .then((sensitive) => setRevealed(sensitive))
      .catch((err) => toast.error(extractErrorMessage(err)));
  };

  const handleHide = () => {
    setRevealed(null);
    reveal.reset();
  };

  const taxId = revealed ? revealed.taxId ?? "—" : investor.taxIdMasked ?? "—";
  const banking = revealed
    ? revealed.bankingInstructions ?? "—"
    : investor.bankingInstructionsMasked ?? "—";

  return (
    <Card>
      <div className="flex items-center justify-between mb-space-3">
        <h3 className="font-display font-bold text-h3 m-0">Sensitive fields</h3>
        {canViewSensitive &&
          (revealed ? (
            <Button variant="text" size="sm" onClick={handleHide}>
              <EyeOff size={14} aria-hidden /> Hide
            </Button>
          ) : (
            <Button
              variant="text"
              size="sm"
              loading={reveal.isPending}
              onClick={() => setConfirmOpen(true)}
            >
              <Eye size={14} aria-hidden /> Reveal
            </Button>
          ))}
      </div>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-space-3 m-0">
        <Field label="Tax ID" value={taxId} mono />
        <Field label="Banking instructions" value={banking} mono />
      </dl>
      {!canViewSensitive && (
        <p className="text-caption text-gray-mid mt-space-3 mb-0 flex items-center gap-space-1">
          <ShieldAlert size={12} aria-hidden /> Revealing these values requires
          the Investor.ViewSensitiveFields permission.
        </p>
      )}

      <ConfirmModal
        open={confirmOpen}
        title="Reveal sensitive fields"
        message="Revealing this investor's tax ID and banking instructions will be logged in the audit trail. Continue?"
        confirmLabel="Reveal"
        onConfirm={handleConfirmReveal}
        onCancel={() => setConfirmOpen(false)}
      />
    </Card>
  );
}

function StatusCard({ investor }: { investor: InvestorMaskedDto }) {
  return (
    <Card>
      <h3 className="font-display font-bold text-h3 m-0 mb-space-3">Status</h3>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-space-3 m-0">
        <div className="flex flex-col">
          <dt className="text-label font-display uppercase tracking-label text-gray-mid">
            Relationship status
          </dt>
          <dd className="m-0 mt-space-1">
            <InvestorStatusBadge status={investor.status} />
          </dd>
        </div>
        <Field
          label="Accreditation basis"
          value={humanizeStatus(investor.accreditationBasis)}
        />
        <Field
          label="Accreditation verified"
          value={formatDate(investor.accreditationVerifiedAt)}
        />
        <Field
          label="AML / KYC verified"
          value={formatDate(investor.amlKycVerifiedAt)}
        />
      </dl>
    </Card>
  );
}

function NotesSummaryCard({ investor }: { investor: InvestorMaskedDto }) {
  return (
    <Card>
      <h3 className="font-display font-bold text-h3 m-0 mb-space-2">Notes</h3>
      {investor.notes ? (
        <p className="text-small text-onyx m-0 whitespace-pre-line line-clamp-3">
          {investor.notes}
        </p>
      ) : (
        <p className="text-small text-gray-mid italic m-0">
          No notes yet. Use the Notes tab to add internal context.
        </p>
      )}
    </Card>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex flex-col">
      <dt className="text-label font-display uppercase tracking-label text-gray-mid">
        {label}
      </dt>
      <dd className={`text-small text-onyx m-0 ${mono ? "font-mono" : ""}`}>
        {value}
      </dd>
    </div>
  );
}
